import { useRouter } from 'expo-router';
import { Platform, RefreshControl, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCarRatings } from '@/api/partners/dashboard/use-car-ratings';
import { getPerformancePeriod } from '@/api/partners/dashboard/use-dashboard-performance';
import { CarRatingsTable } from '@/components/dashboard/car-ratings-table';
import { DashboardFilters } from '@/components/dashboard/dashboard-filters';
import { navigateToDashboardRatings } from '@/components/dashboard/dashboard-route-params';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Text } from '@/ui';
import { Loader } from '@/ui/loader';

type CarRatingsViewProps = {
  monthCount: number;
  onMonthCountChange: (monthCount: number) => void;
};

export function CarRatingsView({
  monthCount,
  onMonthCountChange,
}: CarRatingsViewProps) {
  const { styles } = useStyles(stylesheet);
  const router = useRouter();
  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const period = getPerformancePeriod(monthCount);
  const carRatings = useCarRatings({
    variables: { from: period.from, to: period.to },
  });
  const cars = carRatings.data?.data ?? [];
  const summary = carRatings.data?.summary;
  const carCount = summary?.totalCars ?? cars.length;

  const showInPageBack = Platform.OS === 'web' && isDesktop();
  const backButton = showInPageBack ? (
    <NavigationButton
      type={NavigationButtonTypes.back_with_text}
      onPress={() => {
        navigateToDashboardRatings(router, monthCount);
      }}
      style={styles.backButton}
    />
  ) : null;

  const isInitialLoad = carRatings.isLoading && carRatings.data === undefined;

  if (isInitialLoad) {
    return (
      <View style={styles.loadingContainer}>
        {backButton}
        <View style={styles.loadingBody}>
          <Loader style={styles.loader} />
        </View>
      </View>
    );
  }

  const isPeriodLoading = carRatings.isFetching && carRatings.isPlaceholderData;

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={carRatings.isRefetching}
          onRefresh={() => {
            void carRatings.refetch();
          }}
        />
      }
    >
      {backButton}

      <View style={styles.pageHeader}>
        <View style={styles.titleBlock}>
          <Text type={isCompactLayout ? 'title1' : 'title2'}>Car ratings</Text>
          <Text type="bodyRegular" style={styles.secondary}>
            {`${carCount.toLocaleString()} cars, highest rated first`}
          </Text>
        </View>
      </View>

      <View style={styles.filters}>
        <DashboardFilters
          monthCount={monthCount}
          onMonthCountChange={onMonthCountChange}
          showCarFilter={false}
        />
      </View>

      {isPeriodLoading ? (
        <View style={styles.bodyLoading}>
          <Loader style={styles.loader} />
        </View>
      ) : cars.length === 0 ? (
        <View style={styles.empty}>
          <Text type="bodyRegular">No rated trips in this period</Text>
        </View>
      ) : (
        <CarRatingsTable
          cars={cars}
          period={period}
          reviewScope="all"
          onOpenReservation={(reservationId) => {
            router.push(`/partner/reservations/${reservationId}`);
          }}
        />
      )}
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    paddingTop: {
      xs: 24,
      md: 56,
    },
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingBottom: {
      xs: 32,
      md: 56,
    },
    width: '100%',
  },
  loadingBody: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButton: {
    alignSelf: 'flex-start',
  },
  scroll: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  content: {
    flexGrow: 1,
    paddingTop: {
      xs: 24,
      md: 56,
    },
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingBottom: {
      xs: 32,
      md: 56,
    },
    gap: {
      xs: 24,
      md: 32,
    },
    width: '100%',
  },
  pageHeader: {
    gap: 4,
  },
  titleBlock: {
    gap: 4,
  },
  secondary: {
    color: theme.colors.text.secondary,
  },
  filters: {
    alignItems: 'flex-start',
    position: 'relative',
    zIndex: 3,
  },
  loader: {
    height: 240,
    alignSelf: 'center',
  },
  bodyLoading: {
    minHeight: 240,
    justifyContent: 'center',
    alignItems: 'center',
  },
  empty: {
    paddingVertical: 48,
    alignItems: 'center',
  },
}));
